import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR, { useSWRConfig } from 'swr';
import { sortRevisions, useLabelRevision, usePrintData, useValidation } from '@/pages/print/hooks';
import { useApi } from '@/providers/api';
import { useToastContext } from '@/providers/toast';
import type { LabelRevisionData, PrinterData } from '@/types/types';

import {
  areRequiredLabelFieldsFilled,
  convertDateToISO,
  convertTimeTo24Hour,
  createUniqueId,
  DEFAULT_DATE_FORMAT,
  DEFAULT_TIME_FORMAT,
  formatDateWithInputFormat,
  formatTimeWithInputFormat,
  getTodayAsISO,
  isDate,
  isTime,
} from '@/utils/print';
import { getRegexValidationMessage } from './regex-validation';
import type { FormDetails, RuleEffect } from './types';

// ── Helpers ──────────────────────────────────────────────────────────────

const DEFAULT_TIME = '00:00';

export const parseValue = (type: string, value: string) =>
  isDate(type) || isTime(type) ? value : type === 'String' ? String(value) : Number(value);

const buildDefaultRow = (
  fields: { name: string; type: string; initialValue?: string }[],
  hiddenFields: Set<string>,
  setValueEffects: { fieldName: string; value: string }[]
): Record<string, string | number> => {
  const row: Record<string, string | number> = { id: createUniqueId(), quantity: 1 };
  for (const field of fields) {
    if (hiddenFields.has(field.name)) continue;
    let value: string | number = field.initialValue
      ? parseValue(field.type, field.initialValue)
      : '';
    if (isDate(field.type)) value = !value ? getTodayAsISO() : convertDateToISO(value as string);
    if (isTime(field.type)) value = !value ? DEFAULT_TIME : convertTimeTo24Hour(value as string);
    const effect = setValueEffects.find((e) => e.fieldName === field.name);
    if (effect) value = effect.value;
    row[field.name] = value;
  }
  return row;
};

const resolveRuleEffects = (formDetails: FormDetails | null): RuleEffect[] => {
  if (!formDetails) return [];
  const effects: RuleEffect[] = [];
  for (const rule of formDetails.rules ?? []) {
    if (!rule.enabled) continue;
    for (const action of rule.actions ?? []) {
      const config = (action.config ?? {}) as Record<string, unknown>;
      const fieldName = typeof config.field === 'string' ? config.field : '';
      if (!fieldName) continue;
      effects.push({
        fieldName,
        action: action.type,
        value: action.type === 'set_value' ? String(config.value ?? '') : undefined,
      });
    }
  }
  return effects;
};

// ── Hook ─────────────────────────────────────────────────────────────────

export const useFormPrintData = (formId: string | null) => {
  const { t } = useTranslation();
  const { client } = useApi();
  const { showToast } = useToastContext();

  const {
    printers,
    labels,
    printersLoading,
    labelsLoading,
    printersError,
    labelsError,
    getLabelRevision,
    submitPrintJob,
  } = usePrintData();

  const [selectedPrinter, setSelectedPrinter] = useState<number | null>(null);
  const [selectedRevision, setSelectedRevision] = useState('');
  const [variables, setVariables] = useState<Record<string, string | number>[]>([]);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isPrinting, setIsPrinting] = useState(false);

  const { mutate } = useSWRConfig();

  const {
    data: formDetails = null,
    isLoading: formDetailsLoading,
    error: formDetailsError,
  } = useSWR<FormDetails>(formId ? ['form-details', formId] : null, async ([, id]) => {
    let numericId = id;
    if (!/^\d+$/.test(id)) {
      const listResponse = await client.v1.forms.$get({ query: { limit: '500' } });
      if (!listResponse.ok) throw new Error(`HTTP ${listResponse.status}`);
      const listData = (await listResponse.json()) as {
        items?: Array<{ id: number; publicId: string }>;
      };
      const match = listData.items?.find((form) => form.publicId === id);
      if (!match) throw new Error(t('formPrintPage.error.loadFormDetails'));
      numericId = String(match.id);
    }

    const response = await client.v1.forms[':id'].$get({ param: { id: numericId } });
    if (!response.ok) throw new Error(`HTTP ${response.status} ${response.statusText}`);
    return (await response.json()) as unknown as FormDetails;
  });

  // Fall back to the newest revision until the user picks one that the form offers.
  const activeRevision = formDetails?.labelRevisions?.includes(selectedRevision)
    ? selectedRevision
    : (sortRevisions(formDetails?.labelRevisions ?? [])[0] ?? '');
  const labelRevisionData = useLabelRevision(
    formDetails?.labelId ?? null,
    activeRevision,
    getLabelRevision
  );

  // ── Derived state ────────────────────────────────────────────────────

  const labelRevisionDataWithQuantity = useMemo(() => {
    if (!labelRevisionData) return null;
    // Strip quantity from fields if the label happens to define it — we handle it separately
    const fields = (labelRevisionData.fields || []).filter((f) => f.name !== 'quantity');
    return { ...labelRevisionData, fields };
  }, [labelRevisionData]);

  const ruleEffects = useMemo(() => resolveRuleEffects(formDetails), [formDetails]);

  const hiddenFields = useMemo(
    () => new Set(ruleEffects.filter((e) => e.action === 'hide').map((e) => e.fieldName)),
    [ruleEffects]
  );
  const readonlyFields = useMemo(
    () => new Set(ruleEffects.filter((e) => e.action === 'readonly').map((e) => e.fieldName)),
    [ruleEffects]
  );
  const disabledFields = useMemo(
    () => new Set(ruleEffects.filter((e) => e.action === 'disable').map((e) => e.fieldName)),
    [ruleEffects]
  );
  const setValueEffects = useMemo(
    () =>
      ruleEffects
        .filter((e) => e.action === 'set_value' && e.value !== undefined)
        .map((e) => ({ fieldName: e.fieldName, value: e.value! })),
    [ruleEffects]
  );

  const visibleFields = useMemo(
    () => (labelRevisionDataWithQuantity?.fields ?? []).filter((f) => !hiddenFields.has(f.name)),
    [labelRevisionDataWithQuantity, hiddenFields]
  );

  const variableRegexMap = useMemo(() => {
    if (!formDetails) return new Map<string, string>();
    const map = new Map<string, string>();
    for (const v of formDetails.variables ?? []) {
      if (v.variableRule && v.labelFieldMapping) map.set(v.labelFieldMapping, v.variableRule);
    }
    return map;
  }, [formDetails]);

  const variableDisplayNameMap = useMemo(() => {
    if (!formDetails) return new Map<string, string>();
    const map = new Map<string, string>();
    for (const v of formDetails.variables ?? []) {
      if (v.labelFieldMapping && v.name?.trim()) map.set(v.labelFieldMapping, v.name.trim());
    }
    return map;
  }, [formDetails]);

  const printerOptions = useMemo(() => {
    const items = printers?.items?.filter((p: PrinterData) => !p.deletedAt) ?? [];
    const allowedIds = formDetails?.printerIds?.length ? new Set(formDetails.printerIds) : null;
    return items
      .filter((p: PrinterData) => !allowedIds || allowedIds.has(p.id))
      .map((p: PrinterData) => ({
        value: p.id?.toString() ?? '',
        label: `${p.name} (${p.manufacturer} ${p.model})`,
      }));
  }, [printers, formDetails]);

  const revisionOptions = useMemo(() => {
    const revs = formDetails?.labelRevisions ?? [];
    return sortRevisions(revs).map((r) => ({ value: r, label: r }));
  }, [formDetails]);

  const labelName = useMemo(
    () =>
      (labels as { items?: Array<{ id: number; name: string }> } | undefined)?.items?.find(
        (l) => l.id === formDetails?.labelId
      )?.name ?? '',
    [labels, formDetails]
  );

  const isQuantityOnly = useMemo(() => visibleFields.length === 0, [visibleFields]);

  const { validateField, validateForm } = useValidation(
    labelRevisionDataWithQuantity,
    variableDisplayNameMap
  );

  const areMandatoryFieldsFilled = areRequiredLabelFieldsFilled(
    labelRevisionDataWithQuantity,
    variables
  );

  // Reset user selections whenever the loaded form changes.
  const formIdentity = formDetails?.id ?? null;
  const formPrinterId = formDetails?.printerIds?.[0] ?? null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on form identity; printer id is the seed value, not a trigger.
  useEffect(() => {
    setSelectedPrinter(formPrinterId);
    setSelectedRevision('');
    setVariables([]);
    setValidationErrors({});
  }, [formIdentity]);

  // Initialize variables when label revision changes
  const prevRevisionRef = useRef<LabelRevisionData | null>(null);
  useEffect(() => {
    if (labelRevisionDataWithQuantity === prevRevisionRef.current) return;
    prevRevisionRef.current = labelRevisionDataWithQuantity;

    if (!labelRevisionDataWithQuantity) {
      setVariables([]);
      setValidationErrors({});
      return;
    }

    setVariables([
      buildDefaultRow(labelRevisionDataWithQuantity.fields, hiddenFields, setValueEffects),
    ]);
    setValidationErrors({});
  }, [labelRevisionDataWithQuantity, hiddenFields, setValueEffects]);

  // ── Handlers ─────────────────────────────────────────────────────────

  const handleRevisionChange = useCallback((value: string) => {
    setSelectedRevision(value);
  }, []);

  const handlePrinterChange = useCallback((value: string) => {
    setSelectedPrinter(value ? Number(value) : null);
  }, []);

  const handleVariableChange = useCallback(
    (id: string, fieldName: string, value: string | number) => {
      setVariables((prev) => prev.map((v) => (v.id === id ? { ...v, [fieldName]: value } : v)));
      validateField(fieldName, id, value, (fn) => setValidationErrors((prev) => fn(prev)));

      const regex = variableRegexMap.get(fieldName);
      if (regex) {
        try {
          if (String(value).length > 0 && !new RegExp(regex).test(String(value))) {
            setValidationErrors((prev) => ({
              ...prev,
              [`${fieldName}_${id}`]: getRegexValidationMessage(regex, t),
            }));
          }
        } catch {
          // invalid regex in config
        }
      }
    },
    [validateField, variableRegexMap, t]
  );

  const handleRemoveVariableSet = useCallback((id: string) => {
    setValidationErrors((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        if (key.endsWith(`_${id}`)) delete next[key];
      }
      return next;
    });
    setVariables((prev) => prev.filter((v) => v.id !== id));
  }, []);

  const handleAddVariableSet = useCallback(() => {
    if (!labelRevisionDataWithQuantity?.fields?.length) return;
    setVariables((prev) => [
      ...prev,
      buildDefaultRow(labelRevisionDataWithQuantity.fields, hiddenFields, setValueEffects),
    ]);
  }, [labelRevisionDataWithQuantity, hiddenFields, setValueEffects]);

  const handlePrint = useCallback(async () => {
    if (!selectedPrinter || !formDetails || !activeRevision) {
      showToast(t('newPrintPage.response.fieldsMissing'), 'warning');
      return;
    }

    const validation = validateForm(variables, (fn) => setValidationErrors((prev) => fn(prev)));
    if (!validation?.isValid) {
      showToast(validation?.toastMessage || t('newPrintPage.response.fixValidation'), 'warning');
      return;
    }

    for (const row of variables) {
      for (const [fieldName, regex] of variableRegexMap) {
        const val = String(row[fieldName] ?? '');
        if (val.length > 0) {
          try {
            if (!new RegExp(regex).test(val)) {
              showToast(
                t('newPrintPage.validation.fieldError', {
                  field: variableDisplayNameMap.get(fieldName) ?? fieldName,
                  message: getRegexValidationMessage(regex, t),
                }),
                'warning'
              );
              return;
            }
          } catch {
            // skip
          }
        }
      }
    }

    setIsPrinting(true);
    try {
      const fields = labelRevisionDataWithQuantity?.fields ?? [];
      const preparedVariables = variables.map(({ id, ...rest }) => {
        const row = { ...rest };
        for (const field of fields) {
          if (row[field.name]) {
            if (isDate(field.type))
              row[field.name] = formatDateWithInputFormat(
                row[field.name],
                field.inputFormat || DEFAULT_DATE_FORMAT
              );
            else if (isTime(field.type))
              row[field.name] = formatTimeWithInputFormat(
                row[field.name],
                field.inputFormat || DEFAULT_TIME_FORMAT
              );
          }
        }
        return row;
      });

      const quantity = variables.reduce((acc, v) => {
        const q = Number(v.quantity ?? 0);
        return acc + (Number.isFinite(q) ? q : 0);
      }, 0);

      const result = await submitPrintJob({
        printerId: selectedPrinter,
        labelId: formDetails.labelId,
        labelRevision: activeRevision,
        variables: preparedVariables,
        quantity,
      });

      if (result.status === 'failed') {
        showToast(`Print job failed: ${result.errorMessage}`, 'error');
        return;
      }

      showToast(
        t(
          result.accepted
            ? 'newPrintPage.response.printJobSubmitted'
            : 'newPrintPage.response.printJobSuccess',
          { count: quantity }
        ),
        'success'
      );
      mutate('print-jobs');
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : t('newPrintPage.response.printJobConnectionError'),
        'error'
      );
    } finally {
      setIsPrinting(false);
    }
  }, [
    selectedPrinter,
    formDetails,
    activeRevision,
    variables,
    variableRegexMap,
    variableDisplayNameMap,
    labelRevisionDataWithQuantity,
    validateForm,
    submitPrintJob,
    mutate,
    showToast,
    t,
  ]);

  return {
    isLoading: printersLoading || labelsLoading,
    isError: !!printersError || !!labelsError || !!formDetailsError,
    formDetailsLoading,
    formDetails,
    labelRevisionData,
    labelName,
    isQuantityOnly,
    selectedPrinter,
    selectedRevision: activeRevision,
    printerOptions,
    revisionOptions,
    variables,
    visibleFields,
    readonlyFields,
    disabledFields,
    variableRegexMap,
    variableDisplayNameMap,
    validationErrors,
    ruleEffects,
    isPrinting,
    areMandatoryFieldsFilled,
    handleRevisionChange,
    handlePrinterChange,
    handleVariableChange,
    handleRemoveVariableSet,
    handleAddVariableSet,
    handlePrint,
  };
};
